import { isNil, isArray, get, isString } from 'lodash';
import { Record, RecordOf } from 'immutable';
import isEqual from 'react-fast-compare';
import moment from 'moment';
import * as Sentry from '@sentry/react';
import ISO_COUNTRIES_EN from 'i18n-iso-countries/langs/en.json';
import { DATE_FORMAT } from 'constants/formats.constants';

// UTILS
import { twoDigitZeroPadTransform, fourDigitZeroPadTransform } from 'componentsV2/Fields/Fields.transforms';
import { region, isNilOrEmpty } from 'utils';
import { ReduxAgreementRecordFactory } from 'services/Agreements/factories';
import {
  Agreement,
  AgreementCharge,
  AgreementChargeBaseFields,
  AgreementCustomer,
  ConnectedMetadata,
  DateObject,
  ReduxAgreement,
} from 'services/resources';
import { DISCOUNTS_VALUES } from 'views/FleetSettings/Charges/components';
import { AutofillData } from './types.utils';
import { ChargeCategory, PER_UNIT } from 'constants/charges';
import { convertUnit } from 'utils/units.utils';
import { UNITS } from 'constants/units';
import { convertPercentageToIndex } from 'utils/fuel.utils';
import { AgreementStatus } from 'constants/agreements';

const ITEMIZED_TAXES_KEY = 'itemizedTaxes';

export const getDropdownFormattedDepartments = (departments) =>
  departments.map((department) => ({
    value: department.get('name'),
  }));

export const getResourceIdByName = (resource, name) => resource.find((value) => value.get('name') === name).get('id');

const makeFinancialData = Record<
  Pick<
    Agreement | ReduxAgreement,
    'total' | 'subtotal' | 'balanceDue' | 'balancePaid' | 'discountTotal' | 'itemizedTaxes'
  >
>({
  total: 0,
  subtotal: 0,
  balanceDue: 0,
  balancePaid: 0,
  discountTotal: null,
  itemizedTaxes: [],
});

export const makeAutofillData = Record<AutofillData>({
  daysOpen: 0,
  distanceDriven: 0,
  fuelUsed: 0,
  vehicleFuelTankCapacity: null,
});

const makeCreditCardData = Record({
  creditcardBrand: '',
  creditcardExpMonth: null,
  creditcardExpYear: null,
  creditcardFingerprint: null,
  creditcardLast4: '',
  creditcardPostalCode: '',
  creditcardSource: null,
});

export const getFinancialData = (agreement: RecordOf<ReduxAgreement>) => {
  return makeFinancialData(agreement);
};

/*
  Using calculateFuelUsed instead of agreement.fuelUsed because agreement.fuelUsed
  can be wrong if the fuelLevel on the vehicle does not match the fuelOut
*/
export const calculateFuelUsed = (
  fuelIn: number | '',
  fuelOut: number | '',
  connectedMetadata: { isConnected: boolean; connectedFuelLevel: number | null }
) => {
  let fuelUsed = 0;
  if (fuelIn === '' && fuelOut !== '') {
    // use connected data if available
    if (
      connectedMetadata?.isConnected &&
      !isNil(connectedMetadata?.connectedFuelLevel) &&
      connectedMetadata?.connectedFuelLevel <= fuelOut
    ) {
      fuelUsed = fuelOut - connectedMetadata.connectedFuelLevel;
    }
  } else if (fuelIn !== '' && fuelOut !== '') {
    if (fuelIn <= fuelOut) {
      fuelUsed = fuelOut - fuelIn;
    }
  }
  return fuelUsed;
};

export const calculateTotalKm = (
  odometerIn: number | '',
  odometerOut: number | '',
  connectedMetadata: { isConnected: boolean; connectedOdometer: number | null }
) => {
  let totalKm = 0;
  if (odometerIn === '' && odometerOut !== '') {
    // use connected data if available
    if (
      connectedMetadata?.isConnected &&
      !isNil(connectedMetadata?.connectedOdometer) &&
      connectedMetadata?.connectedOdometer >= odometerOut
    ) {
      totalKm = connectedMetadata.connectedOdometer - odometerOut;
    }
  } else if (odometerIn !== '' && odometerOut !== '') {
    // Both values are entered
    if (odometerIn >= odometerOut) {
      totalKm = odometerIn - odometerOut;
    }
  }

  return totalKm;
};

const isBookedOrReserved = (status) => {
  return status === AgreementStatus.BOOKED || status === AgreementStatus.RESERVED;
};

export const extractDaysOpen = (dateIn: string | null, dateOut: string | null, status: AgreementStatus) => {
  let daysOpen = 0;

  if (isBookedOrReserved(status)) {
    return daysOpen;
  }

  if (dateIn === null) {
    dateIn = new Date().toISOString();
  }

  if (dateIn && dateOut) {
    const momentDateIn = moment(dateIn);
    const momentDateOut = moment(dateOut);
    // making the same calculations that the backend does so it doesn't look weird when
    // this value gets refreshed after a backend save action

    if (momentDateIn > momentDateOut) {
      daysOpen = Math.round(momentDateIn.diff(momentDateOut, 'days', true));
      daysOpen = daysOpen > 0 ? daysOpen : 1;
    }
  }

  return daysOpen;
};

export const getAutofillData = (agreement: ReduxAgreement) => {
  return makeAutofillData({
    daysOpen: extractDaysOpen(agreement.dateIn, agreement.dateOut, agreement.status) || 0,
    distanceDriven: calculateTotalKm(agreement.odometerIn, agreement.odometerOut, agreement.connectedMetadata) || 0,
    fuelUsed: calculateFuelUsed(agreement.fuelIn, agreement.fuelOut, agreement.connectedMetadata) || 0,
    vehicleFuelTankCapacity: agreement.vehicleFuelTankCapacity || 0,
  });
};

export const updateChargeWithAutofill = (charge: AgreementCharge, autofillData: AutofillData) => {
  const updatedCharge = { ...charge };

  if (updatedCharge.quantity === 0 && updatedCharge.autofill) {
    // Time
    if (updatedCharge.quantityUnit === PER_UNIT.day && autofillData.daysOpen !== 0) {
      updatedCharge.quantity = autofillData.daysOpen;
    }

    // Distance
    if (autofillData.distanceDriven !== 0) {
      if (updatedCharge.quantityUnit === PER_UNIT.kilometers) {
        updatedCharge.quantity = autofillData.distanceDriven;
      }
      if (updatedCharge.quantityUnit === PER_UNIT.miles) {
        const miles = convertUnit(autofillData.distanceDriven, UNITS.kilometer, UNITS.mile, 2);
        updatedCharge.quantity = miles;
      }
    }

    // Fuel Volume
    if (autofillData.vehicleFuelTankCapacity && autofillData.fuelUsed !== 0) {
      const liters = autofillData.vehicleFuelTankCapacity * (autofillData.fuelUsed / 100);

      if (updatedCharge.quantityUnit === PER_UNIT.liters) {
        updatedCharge.quantity = parseFloat(liters.toFixed(2));
      }
      if (updatedCharge.quantityUnit === PER_UNIT.usgallons) {
        updatedCharge.quantity = convertUnit(liters, UNITS.liter, UNITS.gallon, 2);
      }
      if (updatedCharge.quantityUnit === PER_UNIT.ukgallons) {
        updatedCharge.quantity = convertUnit(liters, UNITS.liter, UNITS.ukGallon, 2);
      }
    }

    // Fuel Quantity
    if (updatedCharge.quantityUnit === PER_UNIT.eighths && autofillData.fuelUsed !== 0) {
      const percentToEighthsQuantity = convertPercentageToIndex(autofillData.fuelUsed);
      updatedCharge.quantity = percentToEighthsQuantity;
    }
    updatedCharge.subtotal = updatedCharge.quantity * updatedCharge.price;
  }

  return updatedCharge;
};

export const calculateAutofilledCharges = (agreement: ReduxAgreement) => {
  const autofillData = getAutofillData(agreement);
  const initialCharges = agreement.charges;

  return initialCharges.map((charge) => updateChargeWithAutofill(charge, autofillData));
};

export const getSanitizedCharges = (charges) => {
  if (isArray(charges)) {
    return charges.map((item) => {
      const newItem = { ...item };
      newItem.description = isNil(item.description) ? '' : item.description;
      newItem.code = isNil(item.code) ? '' : item.code;
      return newItem;
    });
  }
  return [];
};

export const getCCFromAgreement = (agreement: RecordOf<ReduxAgreement>) => makeCreditCardData(agreement);

export const extractFinancialData = (agreement: RecordOf<ReduxAgreement>) => {
  const financialData = getFinancialData(agreement);
  const creditcard = getCCFromAgreement(agreement);

  return {
    financialData,
    creditcard,
  };
};

export const sanitizeCustomerCountryAndRegion = <T extends AgreementCustomer>(agreement: T) => {
  const sanitizedAgreement = { ...agreement };

  // HACK: We need to sanitize this at the DB level, but this hack will
  // cover most use cases for now.
  if (sanitizedAgreement.customerCountry !== '' && sanitizedAgreement.customerCountry.toLowerCase().match(/canada/g)) {
    sanitizedAgreement.customerCountry = 'CA';
  }

  const matchedCountry = ISO_COUNTRIES_EN.countries[sanitizedAgreement.customerCountry];

  if (!matchedCountry) {
    sanitizedAgreement.customerCountry = '';
    sanitizedAgreement.customerState = '';
  } else if (!region.isRegionInCountry(sanitizedAgreement.customerCountry, sanitizedAgreement.customerState)) {
    sanitizedAgreement.customerState = '';
  }

  return sanitizedAgreement;
};

export const convertDateStringToDateObject = (dateString: string): DateObject => {
  const output = {
    date: '',
    month: '',
    year: '',
  };

  if (isNilOrEmpty(dateString)) {
    return output;
  }

  const date = moment(dateString, DATE_FORMAT);

  output.date = twoDigitZeroPadTransform(date.date());
  output.month = twoDigitZeroPadTransform(date.month() + 1);
  output.year = fourDigitZeroPadTransform(date.year());

  return output;
};

export const sanitizeIncomingCustomerDates = (customer: AgreementCustomer) => {
  const {
    customerLicenseExpires,
    customerDateOfBirth,
    additionalDriverLicenseExpires,
    customerInsurancePolicyExpiry,
    additionalDriverDateOfBirth,
    ...rest
  } = customer;

  const customerLicenseExpiresDateObject = convertDateStringToDateObject(customerLicenseExpires);
  const customerDateOfBirthDateObject = convertDateStringToDateObject(customerDateOfBirth);
  const customerInsurancePolicyExpiryDateObject = convertDateStringToDateObject(customerInsurancePolicyExpiry);
  const additionalDriverLicenseExpiresDateObject = convertDateStringToDateObject(additionalDriverLicenseExpires);
  const additionalDriverDateOfBirthDateObject = convertDateStringToDateObject(additionalDriverDateOfBirth);

  return {
    ...rest,
    customerLicenseExpires: customerLicenseExpiresDateObject,
    customerDateOfBirth: customerDateOfBirthDateObject,
    customerInsurancePolicyExpiry: customerInsurancePolicyExpiryDateObject,
    additionalDriverLicenseExpires: additionalDriverLicenseExpiresDateObject,
    additionalDriverDateOfBirth: additionalDriverDateOfBirthDateObject,
  };
};

export const sanitizeConnectedMetadata = (connectedMetadata: ConnectedMetadata) => {
  const newConnectedMetadata = { ...connectedMetadata };

  if (connectedMetadata.isConnected) {
    const { connectedOdometer } = connectedMetadata;

    if (!isNil(connectedOdometer)) {
      newConnectedMetadata.connectedOdometer = Math.floor(connectedOdometer);
    }
  }

  return newConnectedMetadata;
};

const sanitizeIncomingAgreementCustomer = (incomingCustomer: AgreementCustomer) => {
  const countrySanitizedCustomer = sanitizeCustomerCountryAndRegion(incomingCustomer);
  return sanitizeIncomingCustomerDates(countrySanitizedCustomer);
};

export const sanitizeIncomingAgreement = (incomingAgreement: Agreement) => {
  const { dateIn = null, dateOut = null, dateDue = null } = incomingAgreement;
  const agreement = { ...incomingAgreement };
  const entries = Object.entries(incomingAgreement);

  entries.forEach(([key, value]) => {
    const getVal = () => {
      if (isNil(value)) {
        if (key === ITEMIZED_TAXES_KEY) {
          return [];
        }

        return '';
      }

      return value;
    };

    agreement[key] = getVal();
  });

  agreement.connectedMetadata = sanitizeConnectedMetadata(agreement.connectedMetadata);

  const customerAgreementInfo = {} as AgreementCustomer;
  const customerKeys = Object.keys(agreement).filter((key) => key.match(/^(customer|additionalDriver)/));
  customerKeys.forEach((key) => {
    customerAgreementInfo[key] = agreement[key];
  });

  const sanitizedCustomer = sanitizeIncomingAgreementCustomer(customerAgreementInfo);

  const agreementWithDateObjects: ReduxAgreement = {
    ...agreement,
    ...sanitizedCustomer,
  };

  // @ts-expect-error This comparison appears to be unintentional because the types 'number' and 'string' have no overlap.
  if (agreementWithDateObjects.vehicleSource === '') {
    agreementWithDateObjects.vehicleExteriorColor = '';
    // @ts-expect-error Type 'string' is not assignable to type 'number'.
    agreementWithDateObjects.vehicleStatus = '';
  }

  const charges = getSanitizedCharges(agreementWithDateObjects.charges);

  const { charges: _charges, ...rest } = agreementWithDateObjects;

  return ReduxAgreementRecordFactory({
    ...rest,
    dateIn,
    dateOut,
    dateDue,
    charges,
  });
};

export const sanitizedOutboundDateEntry = (dateObj, fieldName) => {
  if (isNil(dateObj)) {
    return {};
  }

  if (isEqual(dateObj, { year: '', month: '', date: '' })) {
    return { [fieldName]: null };
  }

  const { date, month, year } = dateObj;

  const dateString = `${year}-${month}-${date}`;

  return { [fieldName]: dateString };
};

export const sanitizeOutboundAgreement = (agreement: Partial<ReduxAgreement>): Partial<Agreement> => {
  const {
    customerLicenseExpires,
    customerDateOfBirth,
    customerInsurancePolicyExpiry,
    additionalDriverLicenseExpires,
    additionalDriverDateOfBirth,
    ...rest
  } = agreement;

  const customerLicenseExpiresEntry = sanitizedOutboundDateEntry(customerLicenseExpires, 'customerLicenseExpires');
  const customerDateOfBirthEntry = sanitizedOutboundDateEntry(customerDateOfBirth, 'customerDateOfBirth');
  const customerInsurancePolicyExpiryEntry = sanitizedOutboundDateEntry(
    customerInsurancePolicyExpiry,
    'customerInsurancePolicyExpiry'
  );

  const additionalDriverLicenseExpiresEntry = sanitizedOutboundDateEntry(
    additionalDriverLicenseExpires,
    'additionalDriverLicenseExpires'
  );
  const additionalDriverDateOfBirthEntry = sanitizedOutboundDateEntry(
    additionalDriverDateOfBirth,
    'additionalDriverDateOfBirth'
  );

  return {
    ...rest,
    ...customerLicenseExpiresEntry,
    ...customerDateOfBirthEntry,
    ...customerInsurancePolicyExpiryEntry,
    ...additionalDriverLicenseExpiresEntry,
    ...additionalDriverDateOfBirthEntry,
  };
};

export const logErrorToSentry = (error) => {
  const status = get(error, 'status');

  // just send to sentry server errors otherwise, stash the error
  // information in the breadcrumbs
  if (status >= 500) {
    Sentry.captureException(JSON.stringify(error));
  } else {
    Sentry.addBreadcrumb({
      message: 'Agreement error',
      category: 'action',
      data: error,
    });
  }
};

export const errorHandler = (errors) => {
  const { data } = errors;

  if (typeof data === 'object') {
    return Object.entries(data).reduce((accumulator, [key, val]) => {
      const newAccumulator = { ...accumulator };

      if (isArray(val)) {
        newAccumulator[key] = val[0];
      } else if (isString(val)) {
        newAccumulator[key] = val;
      }

      return newAccumulator;
    }, {});
  }
  return {};
};

/**
 * This function is used to compute the changed charges between the initial charges
 * and the new charges, when no change is detected the redux charges are used.
 * This is done to avoid providing pending "autofilled" quantities to the back-end.
 */
export const computeChangedChargedValues = <T extends AgreementChargeBaseFields>(
  initialCharges: T[],
  newCharges: T[],
  reduxCharges: T[]
): T[] =>
  reduxCharges.reduce((chargeAccumulator, reduxCharge) => {
    const newCharge = newCharges.find((charge) => charge.id === reduxCharge.id);
    const initialCharge = initialCharges.find((charge) => charge.id === reduxCharge.id);

    if (!newCharge) {
      return chargeAccumulator;
    } else if (isEqual(newCharge, initialCharge)) {
      return [...chargeAccumulator, reduxCharge];
    }

    return [...chargeAccumulator, newCharge];
  }, []);

/*
 * This function is used to extract only the changed values from the agreement
 * form values, this is done to avoid sending unchanged values to the back-end.
 */
export const extractOnlyChangedAgreementValues = <
  AgrType extends ReduxAgreement | Agreement | RecordOf<ReduxAgreement> | RecordOf<Agreement>
>(
  initialFormValues: AgrType,
  newValues: AgrType,
  reduxValues: AgrType
): Partial<AgrType> =>
  Object.entries(newValues).reduce((accumulator, [key, value]) => {
    if (key === 'charges') {
      // Special implementation for charges to avoid sending "autofilled" quantities
      const chargesValue = value as AgreementCharge[];
      const computedCharges = computeChangedChargedValues(initialFormValues.charges, chargesValue, reduxValues.charges);

      if (isEqual(reduxValues[key], computedCharges)) {
        // No change to the charges
        return accumulator;
      }

      return {
        ...accumulator,
        charges: computedCharges,
      };
    } else if (isEqual(reduxValues[key], value)) {
      return accumulator;
    }

    return { ...accumulator, [key]: value };
  }, {});

export const convertChargeToNegative = <T extends { price: number }>(charge: T): T => ({
  ...charge,
  price: Math.abs(charge.price) * -1,
});

const convertChargeToPositive = <T extends { price: number }>(charge: T): T => ({
  ...charge,
  price: Math.abs(charge.price),
});

export const convertDiscountsToNegative = <T extends { price: number; category: ChargeCategory }>(
  charges: T[]
): T[] => {
  return charges.map((charge) => {
    if (DISCOUNTS_VALUES.includes(charge.category)) {
      return convertChargeToNegative(charge);
    }

    return charge;
  });
};

export const convertDiscountsToPositive = <T extends { price: number; category: ChargeCategory }>(
  charges: T[]
): T[] => {
  return charges.map((charge) => {
    if (DISCOUNTS_VALUES.includes(charge.category)) {
      return convertChargeToPositive(charge);
    }

    return charge;
  });
};

export const convertAgreementDiscountsToNegative = (agreement: ReduxAgreement): ReduxAgreement => {
  const agreementWithUpdatedDiscounts = { ...agreement };

  agreementWithUpdatedDiscounts.charges = convertDiscountsToNegative(agreement.charges);

  return agreementWithUpdatedDiscounts;
};

export const convertAgreementDiscountsToPositive = <T extends Partial<ReduxAgreement>>(agreement: T): T => {
  const agreementWithUpdatedDiscounts = { ...agreement };

  if (agreement.charges) {
    agreementWithUpdatedDiscounts.charges = convertDiscountsToPositive(agreement.charges);
  }

  return agreementWithUpdatedDiscounts;
};
